import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { Link } from "react-router-dom";
import { faWarehouseFull } from "@fortawesome/pro-light-svg-icons";
import useHostName from "../../../hooks/useHostName";
import { faBox as faBoxSolid, faStore } from "@fortawesome/pro-solid-svg-icons";
import { faCalculator as faCalculatorSolid } from "@fortawesome/pro-solid-svg-icons";
import { faBoxesStacked } from "@fortawesome/pro-solid-svg-icons";
import { useDarkMode } from "../../../hooks";
import usePageListDetails from "../../../hooks/usePageListDetails";
import { SlideMenuProps } from "./types";

const SlideMenu: React.FC<SlideMenuProps> = ({
	isMenuOpen,
	handleToggleMenu,
}) => {
	const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

	const { darkMode } = useDarkMode();
	const [isVisible, setIsVisible] = useState(false);
	const hostName = useHostName();
	const { pageMeta } = usePageListDetails("navigation");
	const hasOrdersPagePermissions =
		pageMeta?.acl?.["sales-orders"]?.["navigation-sales-orders"] ?? false;
	const hasItemsPagePermissions =
		pageMeta?.acl?.["items"]?.["navigation-items"] ?? false;
	const hasVendorItemsPagePermissions =
		pageMeta?.acl?.["vendor-items"]?.["navigation-vendor-items"] ?? false;
	const isDevelopment = process.env.NODE_ENV === "development";
	let menuItems: Record<string, any> = [];

	const decideMenuOptions = () => {
		const menu = [];
		// If the user doesn't have orders permission, return an empty array.
		if (hasOrdersPagePermissions) {
			menu.push({ name: "Orders", link: "/", icon: faCalculatorSolid });
		}

		// If the user has items permissions, add "Items"
		if (hasItemsPagePermissions) {
			menu.push({ name: "Items", link: "/items", icon: faBoxSolid });
		}
		// Only add "Vendor Items" if vendor items permissions are true.
		if (hasVendorItemsPagePermissions) {
			menu.push({
				name: "Vendor Items",
				link: "/vendor-items",
				icon: faStore,
			});
		}
		// menu.push({
		// 	name: "Inventory",
		// 	link: "/inventory",
		// 	icon: faWarehouseFull,
		// });
		return menu;
	};

	if (hostName.hostName === "BANKOFAMERICA") {
		menuItems = [
			{
				name: "Inventory",
				link: "/",
				icon: faWarehouseFull,
			},
		];
	} else if (
		hostName.hostName === "NYCHH" ||
		hostName.hostName === "ENDEAVORHEALTH"
	) {
		menuItems = [
			{
				name: "Orders",
				link: "/",
				icon: faCalculatorSolid,
			},
			{
				name: "Inventory",
				link: "/inventory?purchase-orders_recordsPerPage=15&purchase-orders_page=1&purchase-orders%3Agroup=94f1e621-6eb4-bece-7f67-302ed49bce88",
				icon: faBoxesStacked,
			},
		];
	} else if (
		["COMPASS", "QUAD", "COMPASSCANADA"].includes(hostName.hostName)
	) {
		menuItems = decideMenuOptions();
	} else {
		menuItems = [
			{ name: "Orders", link: "/", icon: faCalculatorSolid },
			...(isDevelopment
				? [
						{
							name: "Items",
							link: "/items",
							icon: faWarehouseFull,
						},
						{
							name: "Shipments",
							link: "/shipments",
							icon: faWarehouseFull,
						},
						{
							name: "Inv",
							link: "/shipments",
							icon: faWarehouseFull,
						},
						{
							name: "Inventory",
							link: "/inventory?purchase-orders_recordsPerPage=15&purchase-orders_page=1&purchase-orders%3Agroup=94f1e621-6eb4-bece-7f67-302ed49bce88&inventory_items%3Agroup=94f1e621-6eb4-bece-7f67-302ed49bce88",
							icon: faWarehouseFull,
						},
					]
				: []),
		];
	}

	useEffect(() => {
		setIsVisible(isMenuOpen);
	}, [isMenuOpen]);

	const handleLinkClick = () => {
		localStorage.removeItem("activeSearchColumn");
		localStorage.removeItem("activeHeader");
		localStorage.removeItem("approvalFilter");
		localStorage.removeItem("activeSortColumns");
		localStorage.removeItem("uuid");
		localStorage.removeItem("orders");
		handleToggleMenu();
	};

	return (
		menuItems && (
			<div
				role={"menu"}
				className={`glass slide-menu text-navy-500 ${
					darkMode ? "bg-primary" : "bg-white"
				} h-full py-14 px-12 text-left transform ${
					isVisible ? "opacity-100" : "opacity-0"
				} transition-opacity duration-300 ${
					isMenuOpen ? "block" : "hidden"
				}`}
			>
				<h4 className="pb-4 font-bold text-xl">TOGa Supply</h4>
				<nav>
					<ul className="pl-2">
						{menuItems.map((item: Record<string, any>) => (
							<li key={item.name}>
								<Link
									className={`p-2 flex items-center ${
										darkMode
											? "hover:bg-accent"
											: "hover:bg-primaryVeryLight"
									}  rounded-md`}
									to={item.link}
									onClick={handleLinkClick}
								>
									<FontAwesomeIcon
										icon={item.icon as IconProp}
										className="mr-4"
									/>
									{item.name}
								</Link>
							</li>
						))}
					</ul>
				</nav>
			</div>
		)
	);
};

export default SlideMenu;
